import { useEffect, useState } from 'react';

interface TWHeroProps {
  onCtaClick: () => void;
}

export default function TWHero({ onCtaClick }: TWHeroProps) {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 80);
    return () => clearTimeout(t);
  }, []);

  return (
    <section className="relative min-h-screen flex items-center overflow-hidden bg-zinc-950">
      {/* Dark abstract background */}
      <div className="absolute inset-0 z-0">
        <div className="absolute inset-0 bg-gradient-to-br from-zinc-900 via-zinc-950 to-black" />
        <div className="absolute top-0 right-0 w-[70vw] h-[70vw] bg-orange-500/5 rounded-full blur-[150px] -translate-y-1/2 translate-x-1/4" />
        <div className="absolute bottom-0 left-0 w-[50vw] h-[50vw] bg-amber-500/3 rounded-full blur-[120px] translate-y-1/2 -translate-x-1/4" />
      </div>

      {/* Subtle grid pattern */}
      <div
        className="absolute inset-0 z-[1] opacity-[0.025] pointer-events-none"
        style={{
          backgroundImage: 'linear-gradient(rgba(249,115,22,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(249,115,22,0.5) 1px, transparent 1px)',
          backgroundSize: '80px 80px',
        }}
      />

      {/* Floating preview card */}
      <div
        className="hidden xl:block absolute right-[8%] top-1/2 -translate-y-1/2 z-[2]"
        style={{
          opacity: visible ? 1 : 0,
          transform: visible ? 'translateY(-50%) translateX(0)' : 'translateY(-50%) translateX(40px)',
          transition: 'opacity 0.8s ease, transform 0.8s ease',
          transitionDelay: '0.4s',
        }}
      >
        <div className="relative w-[420px]">
          {/* Browser mockup */}
          <div className="bg-zinc-900 rounded-xl border border-zinc-800 shadow-2xl overflow-hidden">
            <div className="flex items-center gap-2 px-4 py-3 bg-zinc-800/50 border-b border-zinc-800">
              <div className="flex gap-1.5">
                <div className="w-3 h-3 rounded-full bg-red-500/80" />
                <div className="w-3 h-3 rounded-full bg-amber-500/80" />
                <div className="w-3 h-3 rounded-full bg-emerald-500/80" />
              </div>
              <div className="flex-1 mx-4">
                <div className="bg-zinc-700/50 rounded-md h-5 w-full flex items-center px-3">
                  <span className="text-zinc-500 text-xs">vasafirma.sk</span>
                </div>
              </div>
            </div>
            <div className="p-4 bg-zinc-900">
              <div className="h-32 bg-gradient-to-br from-orange-500/20 to-amber-500/20 rounded-lg mb-3 flex items-center justify-center">
                <i className="ri-macbook-line text-5xl text-orange-400/40" />
              </div>
              <div className="space-y-2">
                <div className="h-4 bg-zinc-800 rounded w-3/4" />
                <div className="h-3 bg-zinc-800/60 rounded w-full" />
                <div className="h-3 bg-zinc-800/60 rounded w-5/6" />
              </div>
              <div className="flex gap-2 mt-4">
                <div className="h-8 bg-orange-500/20 rounded w-24" />
                <div className="h-8 bg-zinc-800 rounded w-24" />
              </div>
            </div>
          </div>
          {/* Floating badge */}
          <div
            className="absolute -bottom-4 -left-4 bg-white rounded-xl p-4 shadow-xl border border-zinc-100"
            style={{
              opacity: visible ? 1 : 0,
              transform: visible ? 'translateY(0) scale(1)' : 'translateY(20px) scale(0.95)',
              transition: 'opacity 0.6s ease, transform 0.6s ease',
              transitionDelay: '0.7s',
            }}
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center">
                <i className="ri-check-line text-emerald-600 text-lg" />
              </div>
              <div>
                <div className="text-zinc-900 font-bold text-sm">Web odovzdaný</div>
                <div className="text-zinc-500 text-xs">za 10 dní</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 lg:py-32">
        <div className="max-w-2xl xl:max-w-xl">
          {/* Urgency badge */}
          <div
            className="inline-flex items-center gap-2.5 bg-orange-500/10 border border-orange-500/20 rounded-full px-4 py-2 mb-8"
            style={{
              opacity: visible ? 1 : 0,
              transform: visible ? 'translateY(0)' : 'translateY(12px)',
              transition: 'opacity 0.5s ease, transform 0.5s ease',
            }}
          >
            <span className="w-2 h-2 rounded-full bg-orange-400 animate-pulse flex-shrink-0" />
            <span className="text-orange-300 text-sm font-semibold">Máme voľné kapacity na apríl</span>
          </div>

          {/* Headline */}
          <div
            style={{
              opacity: visible ? 1 : 0,
              transform: visible ? 'translateY(0)' : 'translateY(24px)',
              transition: 'opacity 0.65s ease, transform 0.65s ease',
              transitionDelay: '0.1s',
            }}
          >
            <h1 className="text-[2.5rem] sm:text-5xl md:text-6xl lg:text-[4rem] font-black text-white leading-[1.05] tracking-tight mb-6">
              Profesionálny web
              <br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-400 to-amber-400">
                do 12 dní
              </span>
            </h1>
          </div>

          {/* Subheadline */}
          <p
            className="text-zinc-400 text-lg sm:text-xl max-w-xl mb-8 leading-relaxed"
            style={{
              opacity: visible ? 1 : 0,
              transform: visible ? 'translateY(0)' : 'translateY(16px)',
              transition: 'opacity 0.65s ease, transform 0.65s ease',
              transitionDelay: '0.22s',
            }}
          >
            Vytvoríme vám web, ktorý prináša zákazníkov. S dizajnom, textami
            a SEO — pripravený na spustenie. Bez skrytých poplatkov.
          </p>

          {/* Social proof avatars */}
          <div
            className="flex items-center gap-4 mb-10"
            style={{
              opacity: visible ? 1 : 0,
              transform: visible ? 'translateY(0)' : 'translateY(16px)',
              transition: 'opacity 0.65s ease, transform 0.65s ease',
              transitionDelay: '0.3s',
            }}
          >
            <div className="flex -space-x-3">
              {[
                'https://readdy.ai/api/search-image?query=professional%20business%20woman%20headshot%20portrait%20neutral%20background%20confident%20smile%20corporate&width=100&height=100&seq=avatar-1&orientation=squarish',
                'https://readdy.ai/api/search-image?query=professional%20business%20man%20headshot%20portrait%20neutral%20background%20friendly%20corporate&width=100&height=100&seq=avatar-2&orientation=squarish',
                'https://readdy.ai/api/search-image?query=professional%20business%20woman%20headshot%20portrait%20neutral%20background%20warm%20smile%20corporate&width=100&height=100&seq=avatar-3&orientation=squarish',
              ].map((src, i) => (
                <div
                  key={i}
                  className="w-10 h-10 rounded-full border-2 border-zinc-950 overflow-hidden bg-zinc-800"
                >
                  <img src={src} alt="" className="w-full h-full object-cover" />
                </div>
              ))}
              <div className="w-10 h-10 rounded-full border-2 border-zinc-950 bg-zinc-800 flex items-center justify-center text-zinc-400 text-xs font-bold">
                +44
              </div>
            </div>
            <div className="text-sm">
              <div className="flex items-center gap-1 text-amber-400">
                {[1,2,3,4,5].map(i => <i key={i} className="ri-star-fill text-sm" />)}
              </div>
              <div className="text-zinc-500">47 spokojných klientov</div>
            </div>
          </div>

          {/* CTAs */}
          <div
            className="flex flex-col sm:flex-row gap-4 mb-10"
            style={{
              opacity: visible ? 1 : 0,
              transform: visible ? 'translateY(0)' : 'translateY(16px)',
              transition: 'opacity 0.65s ease, transform 0.65s ease',
              transitionDelay: '0.38s',
            }}
          >
            <button
              onClick={onCtaClick}
              className="group relative inline-flex items-center justify-center gap-3 bg-orange-500 hover:bg-orange-400 text-white font-black px-8 py-4 rounded-xl transition-all duration-200 whitespace-nowrap cursor-pointer text-base shadow-lg shadow-orange-500/25 hover:shadow-orange-500/40"
            >
              <span>Chcem bezplatnú konzultáciu</span>
              <i className="ri-arrow-right-line text-lg transition-transform duration-200 group-hover:translate-x-1" />
            </button>
            <a
              href="#ukazky"
              onClick={(e) => { e.preventDefault(); document.querySelector('#ukazky')?.scrollIntoView({ behavior: 'smooth' }); }}
              className="inline-flex items-center justify-center gap-2 text-zinc-400 hover:text-white font-semibold px-6 py-4 rounded-xl transition-all duration-200 whitespace-nowrap cursor-pointer text-base"
            >
              <i className="ri-play-circle-line text-lg" />
              Pozrieť ukážky
            </a>
          </div>

          {/* Trust badges */}
          <div
            className="flex flex-wrap items-center gap-x-6 gap-y-2"
            style={{
              opacity: visible ? 1 : 0,
              transition: 'opacity 0.65s ease',
              transitionDelay: '0.5s',
            }}
          >
            {[
              { icon: 'ri-shield-check-line', label: 'Garantovaná kvalita' },
              { icon: 'ri-time-line', label: 'Odpoveď do 2 hodín' },
              { icon: 'ri-lock-unlock-line', label: 'Bez záväzkov' },
            ].map((item) => (
              <div key={item.label} className="flex items-center gap-2">
                <i className={`${item.icon} text-zinc-500 text-sm`} />
                <span className="text-zinc-500 text-sm">{item.label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}